import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Model from '@tripian/model';
import { api } from '@tripian/core';
import {
  changeLoadingPlans,
  changeLoadingReference,
  changePlans,
  changeReference,
  // fetchAlternatives,
  // fetchReactions,
  // fetchReservations,
  clear,
  saveError,
} from '../redux/action/trip';
import useUser from './useUser';
import ICombinedState from '../redux/model/ICombinedState';

const useTrip = () => {
  const [loadingTripAdd, setLoadingTripAdd] = useState<boolean>(false);
  const [loadingTripUpdate, setLoadingTripUpdate] = useState<boolean>(false);
  const [loadingTripDelete, setLoadingTripDelete] = useState<boolean>(false);

  const { loadingTripReference, tripReference } = useSelector((state: ICombinedState) => ({
    loadingTripReference: state.trip.loading.reference,
    tripReference: state.trip.reference,
  }));
  const dispatch = useDispatch();
  const { userTripReferencesFetch } = useUser();

  const tripAdd = useCallback(
    (tripProfile: Model.TripProfile, minDayIndex = 0): Promise<Model.Trip> => {
      setLoadingTripAdd(true);

      return api
        .tripAdd(tripProfile, minDayIndex)
        .then(async (trip: Model.Trip) => {
          await userTripReferencesFetch();
          return trip;
        })

        .catch((tripAddError) => {
          dispatch(saveError('tripAdd', 'Add Trip', tripAddError));
          throw tripAddError;
        })
        .finally(() => {
          setLoadingTripAdd(false);
        });
    },
    [dispatch, userTripReferencesFetch],
  );

  const tripUpdate = useCallback(
    (tripHash: string, tripProfile: Model.TripProfile, minDayIndex = 0): Promise<Model.Trip> => {
      setLoadingTripUpdate(true);

      return api
        .tripUpdate(tripHash, tripProfile, minDayIndex)
        .then(async (trip: Model.Trip) => {
          await userTripReferencesFetch();
          return trip;
        })

        .catch((tripUpdateError) => {
          dispatch(saveError('tripUpdate', 'Update Trip', tripUpdateError));
          throw tripUpdateError;
        })
        .finally(() => {
          setLoadingTripUpdate(false);
        });
    },
    [dispatch, userTripReferencesFetch],
  );

  const tripDelete = useCallback(
    (tripHash: string): Promise<number> => {
      setLoadingTripDelete(true);

      return api
        .tripDelete(tripHash)
        .then(async (deletedTripId: number) => {
          await userTripReferencesFetch();
          return deletedTripId;
        })

        .catch((tripDeleteError) => {
          dispatch(saveError('tripDelete', 'Delete Trip', tripDeleteError));
          throw tripDeleteError;
        })
        .finally(() => {
          setLoadingTripDelete(false);
        });
    },
    [dispatch, userTripReferencesFetch],
  );

  const tripFetchCallback = useCallback(
    (tripData: Model.Trip) => {
      dispatch(
        changeReference({
          id: tripData.id,
          trip_hash: tripData.trip_hash,
          city: tripData.city,
          trip_profile: tripData.trip_profile,
        }),
      );
      dispatch(changePlans(tripData.plans));
    },
    [dispatch],
  );

  const tripFetch = useCallback(
    (hash: string, minDayIndex = 0, force = false): Promise<Model.Trip> => {
      dispatch(changeLoadingReference(true));
      dispatch(changeLoadingPlans(true));

      return api
        .trip(hash, minDayIndex, force)
        .then((trip: Model.Trip) => {
          tripFetchCallback(trip);
          return trip;
        })

        .catch((tripFetchError) => {
          dispatch(saveError('tripFetch', 'Get Trip', tripFetchError));
          throw tripFetchError;
        })
        .finally(() => {
          dispatch(changeLoadingReference(false));
          dispatch(changeLoadingPlans(false));
        });
    },
    [dispatch, tripFetchCallback],
  );

  // const tripFetchWithRelational = async (hash: string, minDayIndex: number = 0, force = false): Promise<Model.Trip> => {
  //   const trip = await tripFetch(hash, minDayIndex, force);
  //   dispatch(fetchAlternatives(minDayIndex));
  //   dispatch(fetchReactions());
  //   dispatch(fetchFavorites());
  //   dispatch(
  //     fetchReservations(
  //       trip.city.id,
  //       moment()
  //         .subtract(1, 'days')
  //         .format('YYYY-MM-DD'),
  //     ),
  //   );
  //   return trip;
  // };

  const tripReferenceFetch = useCallback(
    (hash: string): Promise<Model.TripReference> =>
      api
        .tripRef(hash)
        // .then((tripReferenceData: Model.TripReference) => {
        //   return tripReferenceData;
        // })

        .catch((tripReferenceFetchError) => {
          dispatch(saveError('tripReferenceFetch', 'Get Trip', tripReferenceFetchError));
          throw tripReferenceFetchError;
        })
        .finally(() => {}),
    [dispatch],
  );

  const tripClear = useCallback(() => {
    dispatch(clear());
  }, [dispatch]);

  return {
    loadingTripReference,
    loadingTripAdd,
    loadingTripUpdate,
    loadingTripDelete,
    tripReference,
    tripAdd,
    tripUpdate,
    tripDelete,
    tripFetch /* , tripFetchWithRelational */,
    tripFetchCallback,
    tripReferenceFetch,
    tripClear,
  };
};

export default useTrip;
